import React, { useState, useRef, useEffect } from 'react';
import { motion } from '@/lib/motion';
import { Play, Pause, SpeakerHigh, SpeakerSlash, ArrowsOutSimple, Factory } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'https://files.ihgbrands.com/ihg-ind';

export const FactoryTourSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [videoExists, setVideoExists] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  const videoUrl = `${MEDIA_URL}/generated/factory_tour_web.mp4`;

  // Only check video existence when section is in viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    fetch(videoUrl, { method: 'HEAD' })
      .then(res => {
        setVideoExists(res.ok);
        setIsLoading(false);
      })
      .catch(() => {
        setVideoExists(false);
        setIsLoading(false);
      });
  }, [isVisible, videoUrl]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const processSteps = [
    { step: '01', title: 'CNC LASER CUTTING', desc: 'Precision cutting with fiber laser technology' },
    { step: '02', title: 'WELDING & FABRICATION', desc: 'Expert TIG welding and metal joining' },
    { step: '03', title: 'METAL FINISHING', desc: 'Hand polishing to mirror perfection' },
    { step: '04', title: 'ASSEMBLY', desc: 'Skilled craftsmen assembly' },
    { step: '05', title: 'QUALITY CONTROL', desc: 'Precision measurement verification' },
    { step: '06', title: 'FINAL MASTERPIECE', desc: 'Bespoke sculpture ready for installation' },
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-obsidian" data-testid="factory-tour-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-3 text-bronze text-[10px] md:text-[11px] font-body font-semibold uppercase tracking-[0.25em] mb-3">
              <Factory weight="bold" className="w-4 h-4" />
              VIRTUAL FACTORY TOUR
              <Factory weight="bold" className="w-4 h-4" />
            </span>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-4">
              FROM RAW METAL TO <span className="text-bronze">MASTERPIECE</span>
            </h2>
            <p className="font-body text-sm md:text-base text-white/60 max-w-2xl mx-auto">
              Experience our complete manufacturing journey — witness the transformation from precision cutting to the final bespoke creation.
            </p>
          </motion.div>
        </div>

        {/* Video Player */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative aspect-video bg-black/50 overflow-hidden mb-10"
        >
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 border-2 border-bronze border-t-transparent rounded-full animate-spin" />
            </div>
          ) : videoExists ? (
            <>
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                loop
                muted={isMuted}
                playsInline
                preload="none"
                crossOrigin="anonymous"
                onEnded={() => setIsPlaying(false)}
                onError={(e) => console.error('Video error:', e.target.error)}
                onLoadedData={() => console.log('Video loaded successfully')}
              >
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              {/* Video Overlay */}
              {!isPlaying && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              )}

              {/* Play Button Overlay */}
              {!isPlaying && (
                <motion.button
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center group"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-bronze/90 flex items-center justify-center group-hover:bg-bronze transition-colors">
                    <Play weight="fill" className="w-8 h-8 md:w-10 md:h-10 text-obsidian ml-1" />
                  </div>
                </motion.button>
              )}

              {/* Video Controls */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button type="button"
                      onClick={togglePlay}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                      {isPlaying ? (
                        <Pause weight="fill" className="w-5 h-5 text-white" />
                      ) : (
                        <Play weight="fill" className="w-5 h-5 text-white ml-0.5" />
                      )}
                    </button>
                    <button type="button"
                      onClick={toggleMute}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                      {isMuted ? (
                        <SpeakerSlash weight="bold" className="w-5 h-5 text-white" />
                      ) : (
                        <SpeakerHigh weight="bold" className="w-5 h-5 text-white" />
                      )}
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="text-white/60 text-xs font-body uppercase tracking-wider hidden sm:block">
                      Factory Tour Journey
                    </span>
                    <button type="button"
                      onClick={toggleFullscreen}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                      <ArrowsOutSimple weight="bold" className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Placeholder when video doesn't exist yet */
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-obsidian via-black to-obsidian">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-bronze/20 flex items-center justify-center">
                  <Factory weight="duotone" className="w-12 h-12 text-bronze" />
                </div>
                <div className="absolute -inset-4 border border-bronze/30 rounded-full animate-pulse" />
              </div>
              <h3 className="font-display text-lg md:text-xl font-bold text-white uppercase tracking-wider mb-2">
                COMING SOON
              </h3>
              <p className="font-body text-sm text-white/50 text-center max-w-md px-4">
                Our cinematic factory tour video is being crafted. Experience the complete journey from raw metal to masterpiece.
              </p>
            </div>
          )}
        </motion.div>

        {/* Process Steps */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-10">
          {processSteps.map((item, index) => (
            <motion.div
              key={item.step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 * index }}
              className="relative group"
            >
              <div className="bg-white/5 border border-white/10 p-4 hover:border-bronze/50 transition-colors h-full">
                <span className="font-display text-3xl md:text-4xl font-bold text-bronze/30 group-hover:text-bronze/50 transition-colors">
                  {item.step}
                </span>
                <h4 className="font-display text-[10px] md:text-xs font-bold text-white uppercase tracking-wider mt-2 mb-1">
                  {item.title}
                </h4>
                <p className="font-body text-[10px] text-white/40 leading-relaxed">
                  {item.desc}
                </p>
              </div>
              {/* Connector line (hidden on last item) */}
              {index < processSteps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-px bg-bronze/30" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FactoryTourSection;
